<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 class="text-3xl font-bold text-slate-100">WhatsApp (envio)</h1>
          <p class="text-slate-400 mt-1 text-sm">
            Vários conectores por ambiente. No agente <strong class="text-slate-300">Analista de dados</strong>, escolha o conector
            nos canais de saída.
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold transition"
          @click="openModal(null)"
        >
          Novo conector
        </button>
      </div>

      <div v-if="loadError" class="mb-4 p-4 bg-red-500/10 border border-red-400/40 text-red-300 rounded-lg text-sm">
        {{ loadError }}
      </div>

      <div v-if="loading" class="text-center py-12 text-slate-400">Carregando…</div>

      <ul v-else-if="connectors.length" class="space-y-3">
        <li
          v-for="c in connectors"
          :key="c.id"
          class="rounded-xl border border-cyan-400/25 bg-slate-900/80 backdrop-blur-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <h2 class="text-lg font-semibold text-slate-100">{{ c.name }}</h2>
              <span class="text-xs px-2 py-0.5 rounded-full border border-cyan-400/35 text-cyan-200 bg-cyan-500/10">
                {{ c.provider === 'twilio' ? 'Twilio' : 'Meta Cloud API' }}
              </span>
              <span
                class="text-xs px-2 py-0.5 rounded-full border"
                :class="c.active ? 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10' : 'border-slate-600 text-slate-400'"
              >
                {{ c.active ? 'Ativo' : 'Inativo' }}
              </span>
            </div>
            <p class="text-sm text-slate-400 mt-1 font-mono break-all">
              <template v-if="c.provider === 'twilio'">
                {{ c.twilio_from_number || '—' }} · SID {{ c.twilio_account_sid || '—' }}
              </template>
              <template v-else> ID do número de telefone: {{ c.phone_number_id || '—' }} </template>
            </p>
          </div>
          <div class="flex gap-2 shrink-0">
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-cyan-400/45 text-cyan-200 hover:bg-cyan-400/12"
              @click="openModal(c.id)"
            >
              Editar
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-red-400/45 text-red-200 hover:bg-red-500/15"
              @click="confirmDelete(c)"
            >
              Excluir
            </button>
          </div>
        </li>
      </ul>

      <p v-else class="text-center text-slate-500 py-12 text-sm">Nenhum conector cadastrado.</p>
    </div>

    <div
      v-if="modalOpen"
      class="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-sm overflow-y-auto overflow-x-hidden"
    >
      <div
        class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-xl w-full min-w-0 max-h-[90vh] flex flex-col overflow-hidden my-8"
      >
        <div class="px-6 py-4 bg-[#2f4563] border-b border-cyan-200/25 flex justify-between items-start gap-3 flex-shrink-0">
          <h3 class="text-lg font-semibold text-slate-100">
            {{ editingId ? 'Editar conector Whatsapp' : 'Novo conector Whatsapp' }}
          </h3>
          <button
            type="button"
            class="text-slate-500 hover:text-cyan-300 p-1 -mr-1 transition"
            aria-label="Fechar"
            @click="closeModal"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form class="flex flex-col flex-1 min-h-0" @submit.prevent="openSaveConfirm">
          <div
            class="px-6 py-4 overflow-y-auto flex-1 min-h-0 overflow-x-hidden [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
          >
            <div
              v-if="testResult?.ok"
              class="mb-3 text-sm text-emerald-200 bg-emerald-500/10 border border-emerald-400/35 rounded-lg px-3 py-2 flex items-start gap-2 min-w-0"
            >
              <span class="shrink-0 pt-0.5">✓</span>
              <span class="min-w-0 break-all">{{ testResult.message || 'Enviado.' }}</span>
            </div>
            <div class="space-y-4">
          <div class="mb-1">
            <div
              class="flex flex-wrap gap-3"
              role="radiogroup"
              aria-labelledby="whatsapp-provider-label"
            >
              <label
                :class="[
                  'inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm cursor-pointer transition',
                  form.provider === 'meta_cloud'
                    ? 'border-cyan-400/50 bg-cyan-400/10 text-slate-100 ring-1 ring-cyan-400/25'
                    : 'border-slate-500/80 bg-slate-800/55 text-slate-300 hover:border-slate-500'
                ]"
              >
                <input
                  v-model="form.provider"
                  type="radio"
                  value="meta_cloud"
                  class="h-4 w-4 border-slate-500 bg-slate-800 text-cyan-400 focus:ring-2 focus:ring-cyan-400/40 focus:ring-offset-0 focus:ring-offset-transparent"
                />
                <span>Meta (Cloud API)</span>
              </label>
              <label
                :class="[
                  'inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm cursor-pointer transition',
                  form.provider === 'twilio'
                    ? 'border-cyan-400/50 bg-cyan-400/10 text-slate-100 ring-1 ring-cyan-400/25'
                    : 'border-slate-500/80 bg-slate-800/55 text-slate-300 hover:border-slate-500'
                ]"
              >
                <input
                  v-model="form.provider"
                  type="radio"
                  value="twilio"
                  class="h-4 w-4 border-slate-500 bg-slate-800 text-cyan-400 focus:ring-2 focus:ring-cyan-400/40 focus:ring-offset-0 focus:ring-offset-transparent"
                />
                <span>Twilio</span>
              </label>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Nome <span class="text-red-400">*</span></label>
            <input
              v-model="form.name"
              type="text"
              required
              class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
            />
          </div>

          <template v-if="form.provider === 'meta_cloud'">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">ID do número de telefone <span class="text-red-400">*</span></label>
              <input
                v-model="form.phone_number_id"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Token de acesso <span class="text-red-400">*</span></label>
              <input
                v-model="form.access_token"
                type="password"
                autocomplete="new-password"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                :placeholder="editingId && form.access_token_configured ? 'Deixe em branco para manter' : ''"
              />
              <label class="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input v-model="form.clear_access_token" type="checkbox" class="rounded border-slate-500" />
                Remover token salvo
              </label>
            </div>
          </template>

          <template v-else>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Account SID <span class="text-red-400">*</span></label>
              <input
                v-model="form.twilio_account_sid"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Número remetente (whatsapp:+…) <span class="text-red-400">*</span></label>
              <input
                v-model="form.twilio_from_number"
                type="text"
                placeholder="whatsapp:+14155238886"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Auth token <span class="text-red-400">*</span></label>
              <input
                v-model="form.twilio_auth_token"
                type="password"
                autocomplete="new-password"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                :placeholder="editingId && form.twilio_auth_token_configured ? 'Deixe em branco para manter' : ''"
              />
              <label class="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input v-model="form.clear_twilio_auth_token" type="checkbox" class="rounded border-slate-500" />
                Remover token salvo
              </label>
            </div>
          </template>

          <label class="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input v-model="form.active" type="checkbox" class="rounded border-slate-500" />
            Ativo
          </label>

          <div class="pt-4 border-t border-slate-600/50 space-y-3">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Número de destino para teste</label>
              <div
                class="flex rounded-lg border border-slate-500/80 bg-slate-800/55 overflow-hidden focus-within:ring-2 focus-within:ring-cyan-400/40 focus-within:border-cyan-400/50"
              >
                <input
                  v-model="testTo"
                  type="text"
                  autocomplete="tel"
                  placeholder="+55 11 123456789"
                  class="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-0"
                />
                <button
                  type="button"
                  title="Enviar mensagem de teste"
                  :disabled="testSendLoading || saving"
                  class="shrink-0 border-l border-slate-500/80 px-3 py-2 text-sm font-medium text-cyan-100 bg-slate-800/90 hover:bg-cyan-500/10 disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap"
                  @click="runTestSend"
                >
                  {{ testSendLoading ? 'Enviando…' : 'Enviar teste' }}
                </button>
              </div>
            </div>
          </div>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-slate-600/70 flex flex-col gap-3 flex-shrink-0 bg-slate-700/55">
            <div
              v-if="formError"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissWhatsappFormError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Não foi possível salvar.</span>
              <span class="text-red-100/95 break-words pr-1">{{ formError }}</span>
            </div>
            <div
              v-if="testError"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissWhatsappTestError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Erro no teste.</span>
              <span class="text-red-100/95 break-words pr-1">{{ testError }}</span>
            </div>
            <div class="flex justify-end gap-2">
              <button
                type="submit"
                :disabled="saving"
                class="px-4 py-2 text-sm rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ saving ? 'Salvando…' : 'Salvar' }}
              </button>
              <button
                type="button"
                class="px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800/70"
                @click="closeModal"
              >
                Cancelar
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="saveConfirmOpen"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      @click.self="closeSaveConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">Deseja salvar este conector de WhatsApp?</p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
            @click="confirmSave"
          >
            {{ saving ? 'Salvando…' : 'Sim' }}
          </button>
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition disabled:opacity-50"
            @click="closeSaveConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="deleteTarget"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl max-w-md w-full p-6 shadow-2xl">
        <h3 class="text-lg font-semibold text-slate-100 mb-2">Excluir conector?</h3>
        <p class="text-slate-300 mb-4 text-sm">{{ deleteTarget.name }}</p>
        <div class="flex justify-end gap-2">
          <button type="button" class="px-4 py-2 rounded-lg border border-slate-500 text-slate-200" @click="deleteTarget = null">
            Cancelar
          </button>
          <button
            type="button"
            :disabled="deleting"
            class="px-4 py-2 rounded-lg bg-red-500/20 border border-red-400/40 text-red-200 disabled:opacity-50"
            @click="executeDelete"
          >
            {{ deleting ? 'Excluindo…' : 'Excluir' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi.js'

export default {
  name: 'WhatsappDeliveryConnectors',

  data() {
    return {
      loading: false,
      loadError: '',
      connectors: [],
      modalOpen: false,
      editingId: null,
      saving: false,
      formError: '',
      saveConfirmOpen: false,
      deleteTarget: null,
      deleting: false,
      testTo: '',
      testSendLoading: false,
      testError: '',
      testResult: null,
      form: this.emptyForm()
    }
  },

  mounted() {
    this.load()
  },

  methods: {
    dismissWhatsappFormError() {
      this.formError = ''
    },
    dismissWhatsappTestError() {
      this.testError = ''
    },

    openSaveConfirm() {
      if (this.saving) return
      this.saveConfirmOpen = true
    },

    closeSaveConfirm() {
      if (this.saving) return
      this.saveConfirmOpen = false
    },

    async confirmSave() {
      if (this.saving) return
      await this.saveModal()
    },

    emptyForm() {
      return {
        name: '',
        provider: 'meta_cloud',
        phone_number_id: '',
        access_token: '',
        clear_access_token: false,
        access_token_configured: false,
        twilio_account_sid: '',
        twilio_auth_token: '',
        clear_twilio_auth_token: false,
        twilio_auth_token_configured: false,
        twilio_from_number: '',
        active: true
      }
    },

    async load() {
      const { get } = useApi()
      this.loading = true
      this.loadError = ''
      const res = await get('/api/v1/whatsapp_delivery_connectors')
      this.loading = false
      if (res.error) {
        this.loadError = res.error.response?.data?.errors?.join?.(', ') || 'Erro ao carregar.'
        this.connectors = []
        return
      }
      this.connectors = res.data?.whatsapp_delivery_connectors || []
    },

    resetTestSendFeedback() {
      this.testError = ''
      this.testResult = null
      this.testSendLoading = false
    },

    resetTestUi() {
      this.testTo = ''
      this.resetTestSendFeedback()
    },

    async openModal(id) {
      this.formError = ''
      this.resetTestSendFeedback()
      this.editingId = id
      if (id) {
        const { get } = useApi()
        const res = await get(`/api/v1/whatsapp_delivery_connectors/${id}`)
        if (res.error || !res.data?.whatsapp_delivery_connector) {
          this.formError = 'Não foi possível carregar o conector.'
          return
        }
        const c = res.data.whatsapp_delivery_connector
        this.form = {
          name: c.name || '',
          provider: c.provider === 'twilio' ? 'twilio' : 'meta_cloud',
          phone_number_id: c.phone_number_id || '',
          access_token: '',
          clear_access_token: false,
          access_token_configured: !!c.access_token_configured,
          twilio_account_sid: c.twilio_account_sid || '',
          twilio_auth_token: '',
          clear_twilio_auth_token: false,
          twilio_auth_token_configured: !!c.twilio_auth_token_configured,
          twilio_from_number: c.twilio_from_number || '',
          active: c.active !== false
        }
        this.testTo = c.test_destination || ''
      } else {
        this.form = this.emptyForm()
        this.testTo = ''
      }
      this.modalOpen = true
    },

    closeModal() {
      this.modalOpen = false
      this.editingId = null
      this.saveConfirmOpen = false
      this.formError = ''
      this.resetTestUi()
      this.form = this.emptyForm()
    },

    buildTestSendPayload() {
      const base = {
        to: this.testTo.trim(),
        provider: this.form.provider
      }
      if (this.editingId) base.id = this.editingId
      if (this.form.provider === 'meta_cloud') {
        return {
          whatsapp_delivery_connector: {
            ...base,
            phone_number_id: this.form.phone_number_id.trim(),
            access_token: this.form.access_token,
            clear_access_token: this.form.clear_access_token
          }
        }
      }
      return {
        whatsapp_delivery_connector: {
          ...base,
          twilio_account_sid: this.form.twilio_account_sid.trim(),
          twilio_from_number: this.form.twilio_from_number.trim(),
          twilio_auth_token: this.form.twilio_auth_token,
          clear_twilio_auth_token: this.form.clear_twilio_auth_token
        }
      }
    },

    async runTestSend() {
      if (this.testSendLoading || this.saving) return
      this.testError = ''
      this.testResult = null
      if (!this.testTo?.trim()) {
        this.testError = 'Informe o número de destino com código do país.'
        return
      }
      if (this.form.provider === 'meta_cloud') {
        if (!this.form.phone_number_id?.trim()) {
          this.testError = 'Informe o ID do número de telefone (Meta).'
          return
        }
        if (this.form.clear_access_token) {
          this.testError = 'Não é possível testar com “Remover token salvo” marcado. Desmarque ou salve um token novo.'
          return
        }
        const tok = this.form.access_token?.trim()
        if (!tok && !this.editingId) {
          this.testError = 'Informe o token de acesso para testar.'
          return
        }
        if (!tok && this.editingId && !this.form.access_token_configured) {
          this.testError = 'Informe o token de acesso para testar.'
          return
        }
      } else {
        if (!this.form.twilio_account_sid?.trim() || !this.form.twilio_from_number?.trim()) {
          this.testError = 'Preencha Account SID e número remetente para testar.'
          return
        }
        if (this.form.clear_twilio_auth_token) {
          this.testError =
            'Não é possível testar com “Remover token salvo” marcado. Desmarque ou salve um token novo.'
          return
        }
        const tok = this.form.twilio_auth_token?.trim()
        if (!tok && !this.editingId) {
          this.testError = 'Informe o Auth token para testar.'
          return
        }
        if (!tok && this.editingId && !this.form.twilio_auth_token_configured) {
          this.testError = 'Informe o Auth token para testar.'
          return
        }
      }

      this.testSendLoading = true
      const { post } = useApi()
      const res = await post('/api/v1/whatsapp_delivery_connectors/test_send', this.buildTestSendPayload())
      this.testSendLoading = false
      if (res.error) {
        const d = res.error.response?.data
        this.testError = d?.error || d?.message || 'Falha ao enviar mensagem de teste.'
        return
      }
      if (res.data?.ok) {
        this.testResult = res.data
      } else {
        this.testError = res.data?.error || 'Falha ao enviar mensagem de teste.'
      }
    },

    buildBody() {
      const base = {
        name: this.form.name.trim(),
        provider: this.form.provider,
        active: this.form.active,
        test_destination: this.testTo.trim() || null
      }
      if (this.form.provider === 'meta_cloud') {
        return {
          ...base,
          phone_number_id: this.form.phone_number_id.trim(),
          clear_access_token: this.form.clear_access_token,
          access_token: this.form.clear_access_token ? null : this.form.access_token
        }
      }
      return {
        ...base,
        twilio_account_sid: this.form.twilio_account_sid.trim(),
        twilio_from_number: this.form.twilio_from_number.trim(),
        clear_twilio_auth_token: this.form.clear_twilio_auth_token,
        twilio_auth_token: this.form.clear_twilio_auth_token ? null : this.form.twilio_auth_token
      }
    },

    async saveModal() {
      if (this.form.provider === 'meta_cloud') {
        if (!this.form.phone_number_id?.trim()) {
          this.formError = 'Informe o ID do número de telefone.'
          return
        }
        const tok = this.form.access_token?.trim()
        if (!tok && !this.editingId) {
          this.formError = 'Informe o token de acesso.'
          return
        }
        if (!tok && this.editingId && !this.form.access_token_configured && !this.form.clear_access_token) {
          this.formError = 'Informe o token de acesso.'
          return
        }
      } else {
        if (!this.form.twilio_account_sid?.trim() || !this.form.twilio_from_number?.trim()) {
          this.formError = 'Preencha Account SID e número remetente.'
          return
        }
        const tok = this.form.twilio_auth_token?.trim()
        if (!tok && !this.editingId) {
          this.formError = 'Informe o Auth token.'
          return
        }
        if (!tok && this.editingId && !this.form.twilio_auth_token_configured && !this.form.clear_twilio_auth_token) {
          this.formError = 'Informe o Auth token.'
          return
        }
      }
      this.saving = true
      this.formError = ''
      const { post, put } = useApi()
      const body = { whatsapp_delivery_connector: this.buildBody() }
      let res
      if (this.editingId) {
        res = await put(`/api/v1/whatsapp_delivery_connectors/${this.editingId}`, body)
      } else {
        res = await post('/api/v1/whatsapp_delivery_connectors', body)
      }
      this.saving = false
      if (res.error) {
        const msg = res.error.response?.data?.errors
        this.formError = Array.isArray(msg) ? msg.join(', ') : 'Erro ao salvar.'
        return
      }
      this.saveConfirmOpen = false
      this.closeModal()
      await this.load()
    },

    confirmDelete(c) {
      this.deleteTarget = c
    },

    async executeDelete() {
      if (!this.deleteTarget) return
      const id = this.deleteTarget.id
      this.deleting = true
      const { delete: del } = useApi()
      const res = await del(`/api/v1/whatsapp_delivery_connectors/${id}`)
      this.deleting = false
      this.deleteTarget = null
      if (!res.error) {
        this.connectors = this.connectors.filter((c) => String(c.id) !== String(id))
      }
    }
  }
}
</script>
